import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { GET_LOYALTY_PROGRAM, GET_LOYALTY_PROGRAMS } from '../../graphql/loyalty-queries'
import {
  CLONE_LOYALTY_PROGRAM,
  UPDATE_LOYALTY_PROGRAM_DRAFT,
  UPSERT_LOYALTY_REWARD_TIER,
  DELETE_LOYALTY_REWARD_TIER,
} from '../../graphql/loyalty-mutations'
import { GET_CATEGORIES } from '../../graphql/product-queries'
import type {
  FreeItemParams,
  FreeMealParams,
  LoyaltyProgram,
  LoyaltyRewardParams,
  LoyaltyRewardTier,
  LoyaltyRewardTierInput,
  LoyaltyRewardType,
  LoyaltyTierTrigger,
  UpgradeParams,
} from '../../types/loyalty'
import type { Category } from '../../types/product'
import { formatEuros } from '../../lib/format'
import LoadingSpinner from '../../components/LoadingSpinner'
import CategoryAssignmentPicker from '../../components/CategoryAssignmentPicker'
import ProductPicker from '../../components/loyalty/ProductPicker'
import SizeSelect from '../../components/SizeSelect'
import { PublishDialog } from './programDialogs'
import { MAX_NAME_LENGTH, StatusPill } from './shared'

// P05 — the draft editor: the five rules on the left, the reward ladder on the
// right. Everything is editable only while the programme is a DRAFT; once
// published the rules are frozen and the way to change them is to clone.
//
// Mutation failures are toasted by the global errorLink in src/lib/apollo.ts —
// no toast.error here, it would double.

const REWARD_TYPE_LABEL: Record<LoyaltyRewardType, string> = {
  UPGRADE: 'Upgrade',
  FREE_ITEM: 'Free item',
  FREE_MEAL: 'Free meal',
}

const MAX_VALIDITY_DAYS = 730
const DEFAULT_VALIDITY_DAYS = 180

/** Raw input → gross cents. Empty = 0; a Dutch comma is accepted. `null` = unparseable. */
const parseMoney = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (!trimmed) return 0
  // Strict shape, not bare parseFloat: "1.250,00" would silently become
  // € 1,25 and "2e3" € 2000 — reject anything but digits + optional 2 decimals.
  if (!/^\d+([.,]\d{1,2})?$/.test(trimmed)) return null
  return Math.round(parseFloat(trimmed.replace(',', '.')) * 100)
}

/** Cents → the string the money inputs hold. */
const centsToInput = (cents: number): string => (cents > 0 ? (cents / 100).toFixed(2) : '')

/** "12" → 12, anything else → null (so a comparator never sees NaN). */
const parseWholeNumber = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (!/^\d+$/.test(trimmed)) return null
  return parseInt(trimmed, 10)
}

/** WELCOME first, then by stamp threshold ascending — the order a member climbs. */
const sortTiers = (tiers: LoyaltyRewardTier[]): LoyaltyRewardTier[] =>
  [...tiers].sort((a, b) => {
    if (a.trigger !== b.trigger) return a.trigger === 'WELCOME' ? -1 : 1
    return (a.threshold ?? 0) - (b.threshold ?? 0)
  })

const asUpgrade = (params: LoyaltyRewardParams) => params as UpgradeParams
const asFreeItem = (params: LoyaltyRewardParams) => params as FreeItemParams
const asFreeMeal = (params: LoyaltyRewardParams) => params as FreeMealParams

// ===========================================================================
// Rung form
// ===========================================================================

type RungErrors = Partial<
  Record<
    'threshold' | 'name_nl' | 'name_en' | 'validity_days' | 'sizes' | 'scope' | 'max_cents',
    string
  >
>

interface RungFormProps {
  program: LoyaltyProgram
  /** null = adding a rung. */
  tier: LoyaltyRewardTier | null
  categories: Category[]
  onClose: () => void
}

/**
 * Add or replace one rung. Declared at module level on purpose: a component
 * defined inside its parent remounts its whole subtree on every parent render,
 * which makes taps on a polling page "sometimes not register".
 *
 * The caller mounts it with a key, so opening another rung resets the state.
 */
const RungForm = ({ program, tier, categories, onClose }: RungFormProps) => {
  const upgrade = tier && tier.reward_type === 'UPGRADE' ? asUpgrade(tier.params_json) : null
  const freeItem = tier && tier.reward_type === 'FREE_ITEM' ? asFreeItem(tier.params_json) : null
  const freeMeal = tier && tier.reward_type === 'FREE_MEAL' ? asFreeMeal(tier.params_json) : null

  const [trigger, setTrigger] = useState<LoyaltyTierTrigger>(tier?.trigger ?? 'STAMP')
  const [threshold, setThreshold] = useState(tier?.threshold != null ? String(tier.threshold) : '')
  const [rewardType, setRewardType] = useState<LoyaltyRewardType>(tier?.reward_type ?? 'UPGRADE')
  const [nameNl, setNameNl] = useState(tier?.name_nl ?? '')
  const [nameEn, setNameEn] = useState(tier?.name_en ?? '')
  const [validityDays, setValidityDays] = useState(
    String(tier?.validity_days ?? DEFAULT_VALIDITY_DAYS)
  )
  const [active, setActive] = useState(tier?.active ?? true)
  const [fromSize, setFromSize] = useState(upgrade?.from_size ?? '')
  const [toSize, setToSize] = useState(upgrade?.to_size ?? '')
  const [maxValue, setMaxValue] = useState(freeMeal ? centsToInput(freeMeal.max_cents) : '')
  const [categoryIds, setCategoryIds] = useState<number[]>(
    upgrade?.category_ids ?? freeMeal?.category_ids ?? []
  )
  const [productIds, setProductIds] = useState<number[]>(
    upgrade?.product_ids ?? freeItem?.product_ids ?? freeMeal?.product_ids ?? []
  )
  const [errors, setErrors] = useState<RungErrors>({})

  const [upsertTier, { loading: saving }] = useMutation(UPSERT_LOYALTY_REWARD_TIER)

  // A programme has exactly one welcome rung; offering the radio when another
  // rung already holds it would only earn a refusal.
  const welcomeTaken = program.tiers.some((other) => other.trigger === 'WELCOME' && other.id !== tier?.id)

  const toggleCategory = (categoryId: number, assigned: boolean) => {
    setCategoryIds((ids) =>
      assigned ? [...ids, categoryId] : ids.filter((id) => id !== categoryId)
    )
  }

  const handleSave = async () => {
    const nextErrors: RungErrors = {}

    let thresholdValue: number | null = null
    if (trigger === 'STAMP') {
      thresholdValue = parseWholeNumber(threshold)
      if (thresholdValue === null || thresholdValue < 1 || thresholdValue > program.card_size) {
        nextErrors.threshold = `Enter a stamp count between 1 and ${program.card_size}.`
      }
    }

    if (!nameNl.trim()) nextErrors.name_nl = 'A Dutch name is required.'
    else if (nameNl.trim().length > MAX_NAME_LENGTH) nextErrors.name_nl = 'At most 100 characters.'
    if (!nameEn.trim()) nextErrors.name_en = 'An English name is required.'
    else if (nameEn.trim().length > MAX_NAME_LENGTH) nextErrors.name_en = 'At most 100 characters.'

    const validity = parseWholeNumber(validityDays)
    if (validity === null || validity < 1 || validity > MAX_VALIDITY_DAYS) {
      nextErrors.validity_days = `Enter a number of days between 1 and ${MAX_VALIDITY_DAYS}.`
    }

    let maxCents: number | null = null
    if (rewardType === 'UPGRADE') {
      const from = fromSize.trim()
      const to = toSize.trim()
      if (!from || !to) {
        nextErrors.sizes = 'Both sizes are required.'
      } else if (from.toLowerCase() === to.toLowerCase()) {
        // Sizes are matched lower-cased, so "Medium" → "medium" upgrades nothing.
        nextErrors.sizes = 'The two sizes must differ.'
      }
      if (categoryIds.length === 0 && productIds.length === 0) {
        nextErrors.scope = 'Pick at least one category or product.'
      }
    } else if (rewardType === 'FREE_ITEM') {
      if (productIds.length === 0) nextErrors.scope = 'Pick at least one product.'
    } else {
      maxCents = parseMoney(maxValue)
      if (maxCents === null || maxCents <= 0) {
        nextErrors.max_cents = 'Enter an amount above 0.00.'
      }
      if (categoryIds.length === 0 && productIds.length === 0) {
        nextErrors.scope = 'Pick at least one category or product.'
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    setErrors({})

    // Only the keys the reward type allows — the API refuses an unknown one.
    // Empty id lists are omitted rather than sent as [].
    const scope = {
      ...(categoryIds.length > 0 ? { category_ids: categoryIds } : {}),
      ...(productIds.length > 0 ? { product_ids: productIds } : {}),
    }
    const params: LoyaltyRewardParams =
      rewardType === 'UPGRADE'
        ? { from_size: fromSize.trim(), to_size: toSize.trim(), ...scope }
        : rewardType === 'FREE_ITEM'
          ? { product_ids: productIds }
          : { max_cents: maxCents as number, ...scope }

    // sort_order is derived, not typed: the API orders a ladder by sort_order
    // alone (no threshold tiebreak), so every client sees WELCOME first and
    // then the rungs in stamp order without re-sorting.
    const input: LoyaltyRewardTierInput = {
      ...(tier ? { id: tier.id } : {}),
      trigger,
      ...(trigger === 'STAMP' ? { threshold: thresholdValue as number } : {}),
      reward_type: rewardType,
      params_json: params,
      validity_days: validity as number,
      name_nl: nameNl.trim(),
      name_en: nameEn.trim(),
      sort_order: trigger === 'STAMP' ? (thresholdValue as number) : 0,
      active,
    }

    try {
      await upsertTier({ variables: { program_id: program.id, input } })
      toast.success(tier ? 'Rung updated' : 'Rung added')
      onClose()
    } catch {
      // errorLink already toasted and its message names the field — keep the
      // form open so the entry is not lost.
    }
  }

  const scopeBlock = (withCategories: boolean) => (
    <div className="space-y-3">
      {withCategories && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
          <CategoryAssignmentPicker
            allCategories={categories}
            assignedCategoryIds={new Set(categoryIds)}
            onChange={toggleCategory}
          />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Products</label>
        <ProductPicker selectedIds={productIds} onChange={setProductIds} />
      </div>
      {errors.scope && <p className="text-xs text-red-600">{errors.scope}</p>}
    </div>
  )

  return (
    <div className="card space-y-4">
      <h3 className="text-base font-semibold text-gray-900">{tier ? 'Edit rung' : 'Add rung'}</h3>

      {/* Trigger */}
      <div>
        <span className="block text-sm font-medium text-gray-700 mb-1">Unlocked by</span>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="rung-trigger"
              checked={trigger === 'WELCOME'}
              disabled={welcomeTaken}
              onChange={() => setTrigger('WELCOME')}
              className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-60"
            />
            Signing up (welcome)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="rung-trigger"
              checked={trigger === 'STAMP'}
              onChange={() => setTrigger('STAMP')}
              className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            A stamp count
          </label>
        </div>
        {welcomeTaken && (
          <p className="mt-1 text-xs text-gray-500">
            This program already has a welcome rung — a program has exactly one.
          </p>
        )}
      </div>

      {/* Threshold */}
      {trigger === 'STAMP' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            At stamp <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={1}
            max={program.card_size}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            className={`input-field ${errors.threshold ? 'border-red-300 bg-red-50' : ''}`}
          />
          {errors.threshold ? (
            <p className="mt-1 text-xs text-red-600">{errors.threshold}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-400">
              1 to {program.card_size}. Each stamp count carries at most one rung.
            </p>
          )}
        </div>
      )}

      {/* Reward type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Reward</label>
        <select
          value={rewardType}
          onChange={(e) => {
            setRewardType(e.target.value as LoyaltyRewardType)
            setErrors({})
          }}
          className="input-field"
        >
          <option value="UPGRADE">Upgrade one size</option>
          <option value="FREE_ITEM">A free item</option>
          <option value="FREE_MEAL">A free meal up to an amount</option>
        </select>
      </div>

      {/* Names */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name (nl) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            maxLength={MAX_NAME_LENGTH}
            value={nameNl}
            onChange={(e) => setNameNl(e.target.value)}
            placeholder="Gratis upgrade"
            className={`input-field ${errors.name_nl ? 'border-red-300 bg-red-50' : ''}`}
          />
          {errors.name_nl && <p className="mt-1 text-xs text-red-600">{errors.name_nl}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name (en) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            maxLength={MAX_NAME_LENGTH}
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            placeholder="Free upgrade"
            className={`input-field ${errors.name_en ? 'border-red-300 bg-red-50' : ''}`}
          />
          {errors.name_en && <p className="mt-1 text-xs text-red-600">{errors.name_en}</p>}
        </div>
      </div>

      {/* Reward parameters */}
      {rewardType === 'UPGRADE' && (
        <div className="space-y-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From size <span className="text-red-500">*</span>
              </label>
              <SizeSelect
                value={fromSize}
                onChange={setFromSize}
                invalid={Boolean(errors.sizes)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To size <span className="text-red-500">*</span>
              </label>
              <SizeSelect
                value={toSize}
                onChange={setToSize}
                invalid={Boolean(errors.sizes)}
              />
            </div>
          </div>
          {errors.sizes ? (
            <p className="text-xs text-red-600">{errors.sizes}</p>
          ) : (
            <p className="text-xs text-gray-400">
              A variant&rsquo;s size, e.g. medium → large. Pick from the sizes in use or add a new one.
            </p>
          )}
          {scopeBlock(true)}
        </div>
      )}

      {rewardType === 'FREE_ITEM' && scopeBlock(false)}

      {rewardType === 'FREE_MEAL' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max value <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={maxValue}
              onChange={(e) => setMaxValue(e.target.value)}
              placeholder="12.50"
              className={`input-field ${errors.max_cents ? 'border-red-300 bg-red-50' : ''}`}
            />
            {errors.max_cents ? (
              <p className="mt-1 text-xs text-red-600">{errors.max_cents}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-400">Including VAT.</p>
            )}
          </div>
          {scopeBlock(true)}
        </div>
      )}

      {/* Validity + active */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Valid for (days) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={1}
            max={MAX_VALIDITY_DAYS}
            value={validityDays}
            onChange={(e) => setValidityDays(e.target.value)}
            className={`input-field ${errors.validity_days ? 'border-red-300 bg-red-50' : ''}`}
          />
          {errors.validity_days ? (
            <p className="mt-1 text-xs text-red-600">{errors.validity_days}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-400">
              How long the reward stays in the wallet once unlocked.
            </p>
          )}
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 pb-2">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Active (members can unlock this rung)
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
        <button onClick={onClose} disabled={saving} className="btn-secondary">
          Cancel
        </button>
        <button onClick={() => void handleSave()} disabled={saving} className="btn-primary">
          {saving ? 'Saving…' : 'Save rung'}
        </button>
      </div>
    </div>
  )
}

// ===========================================================================
// Page
// ===========================================================================

type RulesForm = {
  name: string
  cardSize: string
  minSpend: string
  maxStamps: string
  claimTtl: string
}

const rulesFormOf = (program: LoyaltyProgram): RulesForm => ({
  name: program.name,
  cardSize: String(program.card_size),
  minSpend: (program.min_order_cents / 100).toFixed(2),
  maxStamps: String(program.max_stamps_per_day),
  claimTtl: String(program.claim_ttl_days),
})

type RulesErrors = Partial<Record<keyof RulesForm, string>>

const LoyaltyProgramEditorPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, loading, error } = useQuery(GET_LOYALTY_PROGRAM, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    skip: !id,
  })
  const program: LoyaltyProgram | null = data?.loyaltyProgram ?? null

  // Only for the publish dialog's "Closes “<current name>” at that moment."
  // line — the single-programme query cannot know which one is live.
  const { data: listData } = useQuery(GET_LOYALTY_PROGRAMS)
  const currentProgram: LoyaltyProgram | null =
    (listData?.loyaltyPrograms ?? []).find((p: LoyaltyProgram) => p.is_current) ?? null

  const { data: categoryData } = useQuery(GET_CATEGORIES)
  const categories: Category[] = categoryData?.categories ?? []

  // The rules form holds the operator's UNSAVED override, or null for "what
  // the server has". Deriving the shown values instead of copying the
  // programme into state on mount keeps two things honest: there is no render
  // in which the form is not ready yet, and a ladder mutation — every one of
  // them returns the whole programme — cannot silently wipe rules that are
  // still being typed.
  const [form, setForm] = useState<RulesForm | null>(null)
  const [errors, setErrors] = useState<RulesErrors>({})
  const [publishing, setPublishing] = useState(false)
  // null = closed, { tier: null } = adding, { tier } = editing that rung.
  const [rungForm, setRungForm] = useState<{ tier: LoyaltyRewardTier | null } | null>(null)

  // Navigating to another programme (a clone opens its own editor) drops the
  // previous one's unsaved edits rather than carrying them over.
  const programId = program?.id
  useEffect(() => {
    setForm(null)
    setErrors({})
  }, [programId])

  const [updateDraft, { loading: savingRules }] = useMutation(UPDATE_LOYALTY_PROGRAM_DRAFT)
  // The clone is a new row: the list is re-read, exactly as the list page does.
  const [cloneProgram] = useMutation(CLONE_LOYALTY_PROGRAM, {
    refetchQueries: [GET_LOYALTY_PROGRAMS],
  })
  const [deleteTier] = useMutation(DELETE_LOYALTY_REWARD_TIER)

  if (loading && !program) {
    return (
      <div className="flex items-center justify-center h-48">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // A failed read is not a missing programme: say which one it was.
  if (!program && error) {
    return (
      <div className="space-y-4">
        <Link
          to="/loyalty/programs"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Programs
        </Link>
        <div className="text-center py-12 text-red-600">{error.message}</div>
      </div>
    )
  }

  if (!program) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
        <h3 className="text-sm font-semibold text-gray-900">Program not found</h3>
        <p className="mt-2 text-sm text-gray-500">
          It may have been deleted, or the link points at an id that does not exist.
        </p>
        <Link to="/loyalty/programs" className="btn-primary mt-6 inline-flex items-center gap-2">
          <ArrowLeftIcon className="h-5 w-5" />
          Programs
        </Link>
      </div>
    )
  }

  const isDraft = program.status === 'DRAFT'
  const savedForm = rulesFormOf(program)
  const activeForm = form ?? savedForm
  const dirty = (Object.keys(savedForm) as (keyof RulesForm)[]).some(
    (key) => savedForm[key] !== activeForm[key]
  )

  const handleSaveRules = async () => {
    const nextErrors: RulesErrors = {}

    const name = activeForm.name.trim()
    if (!name) nextErrors.name = 'A name is required.'
    else if (name.length > MAX_NAME_LENGTH) nextErrors.name = 'At most 100 characters.'

    const cardSize = parseWholeNumber(activeForm.cardSize)
    if (cardSize === null || cardSize < 1 || cardSize > 50) {
      nextErrors.cardSize = 'Enter a number between 1 and 50.'
    } else {
      // The API only checks this at publish; catching it here names the rung
      // instead of leaving a "threshold must be between 1 and N" toast to decode.
      const highest = Math.max(
        0,
        ...program.tiers.filter((t) => t.trigger === 'STAMP').map((t) => t.threshold ?? 0)
      )
      if (highest > cardSize) {
        nextErrors.cardSize = `A rung sits at stamp ${highest} — move or remove it before shrinking the card.`
      }
    }

    const minSpend = parseMoney(activeForm.minSpend)
    if (minSpend === null) nextErrors.minSpend = 'Enter an amount of 0.00 or more.'

    const maxStamps = parseWholeNumber(activeForm.maxStamps)
    if (maxStamps === null || maxStamps < 1 || maxStamps > 10) {
      nextErrors.maxStamps = 'Enter a number between 1 and 10.'
    }

    const claimTtl = parseWholeNumber(activeForm.claimTtl)
    if (claimTtl === null || claimTtl < 1 || claimTtl > 90) {
      nextErrors.claimTtl = 'Enter a number of days between 1 and 90.'
    }

    if (
      Object.keys(nextErrors).length > 0 ||
      cardSize === null ||
      minSpend === null ||
      maxStamps === null ||
      claimTtl === null
    ) {
      setErrors(nextErrors)
      return
    }
    setErrors({})

    try {
      await updateDraft({
        variables: {
          id: program.id,
          input: {
            name,
            card_size: cardSize,
            min_order_cents: minSpend,
            max_stamps_per_day: maxStamps,
            claim_ttl_days: claimTtl,
          },
        },
      })
      // Drop the override: the fields now show what the server stored (it
      // trims the name), so "Save draft" goes back to disabled instead of
      // staying dirty on whitespace alone.
      setForm(null)
      toast.success('Draft saved')
    } catch {
      // errorLink already toasted; the form keeps what was typed.
    }
  }

  const handleClone = async () => {
    try {
      const result = await cloneProgram({ variables: { id: program.id } })
      const clone: LoyaltyProgram | undefined = result.data?.cloneLoyaltyProgram
      if (!clone) return
      toast.success('Draft created from the clone')
      navigate(`/loyalty/programs/${clone.id}`)
    } catch {
      // errorLink already toasted.
    }
  }

  const handleDeleteTier = async (tier: LoyaltyRewardTier) => {
    const label = tier.trigger === 'WELCOME' ? 'the welcome rung' : `the rung at stamp ${tier.threshold}`
    if (!window.confirm(`Remove ${label} (“${tier.name_nl}”)? It is gone from this draft.`)) return
    try {
      await deleteTier({ variables: { id: tier.id } })
      toast.success('Rung removed')
      if (rungForm?.tier?.id === tier.id) setRungForm(null)
    } catch {
      // errorLink already toasted.
    }
  }

  const tiers = sortTiers(program.tiers)

  const readOnlyRow = (label: string, value: string) => (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right">{value}</span>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link
            to="/loyalty/programs"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Programs
          </Link>
          <div className="mt-1 flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">{program.name}</h1>
            <StatusPill status={program.status} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isDraft && (
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={() => setPublishing(true)}
                disabled={dirty}
                className="btn-primary disabled:opacity-50"
              >
                Publish…
              </button>
              {dirty && <p className="text-xs text-gray-500">Save the draft first.</p>}
            </div>
          )}
          {!isDraft && (
            <button
              onClick={() => void handleClone()}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <DocumentDuplicateIcon className="h-5 w-5" />
              Clone
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[340px,1fr]">
        {/* Rules */}
        <div className="card space-y-4">
          <h2 className="text-base font-semibold text-gray-900">Rules (frozen once published)</h2>

          {!isDraft && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600">
              {program.status === 'ENDED'
                ? 'This program has ended.'
                : 'Published programs are frozen. Clone to change the rules.'}
            </div>
          )}

          {isDraft ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  maxLength={MAX_NAME_LENGTH}
                  value={activeForm.name}
                  onChange={(e) => setForm({ ...activeForm, name: e.target.value })}
                  className={`input-field ${errors.name ? 'border-red-300 bg-red-50' : ''}`}
                />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Card size <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={activeForm.cardSize}
                  onChange={(e) => setForm({ ...activeForm, cardSize: e.target.value })}
                  className={`input-field ${errors.cardSize ? 'border-red-300 bg-red-50' : ''}`}
                />
                {errors.cardSize ? (
                  <p className="mt-1 text-xs text-red-600">{errors.cardSize}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">Stamps on a full card, 1 to 50.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min food spend <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={activeForm.minSpend}
                  onChange={(e) => setForm({ ...activeForm, minSpend: e.target.value })}
                  placeholder="8.00"
                  className={`input-field ${errors.minSpend ? 'border-red-300 bg-red-50' : ''}`}
                />
                {errors.minSpend ? (
                  <p className="mt-1 text-xs text-red-600">{errors.minSpend}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">
                    Food total after discount that earns a stamp, incl. VAT.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max stamps / day <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={activeForm.maxStamps}
                  onChange={(e) => setForm({ ...activeForm, maxStamps: e.target.value })}
                  className={`input-field ${errors.maxStamps ? 'border-red-300 bg-red-50' : ''}`}
                />
                {errors.maxStamps ? (
                  <p className="mt-1 text-xs text-red-600">{errors.maxStamps}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">1 to 10.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Claim window (days) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={activeForm.claimTtl}
                  onChange={(e) => setForm({ ...activeForm, claimTtl: e.target.value })}
                  className={`input-field ${errors.claimTtl ? 'border-red-300 bg-red-50' : ''}`}
                />
                {errors.claimTtl ? (
                  <p className="mt-1 text-xs text-red-600">{errors.claimTtl}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">
                    How long a receipt stays claimable, 1 to 90.
                  </p>
                )}
              </div>

              <div className="flex justify-end border-t border-gray-100 pt-4">
                <button
                  onClick={() => void handleSaveRules()}
                  disabled={savingRules || !dirty}
                  className="btn-primary disabled:opacity-50"
                >
                  {savingRules ? 'Saving…' : 'Save draft'}
                </button>
              </div>
            </>
          ) : (
            <div>
              {readOnlyRow('Name', program.name)}
              {readOnlyRow('Card size', `${program.card_size} stamps`)}
              {readOnlyRow('Min food spend', formatEuros(program.min_order_cents))}
              {readOnlyRow('Max stamps / day', String(program.max_stamps_per_day))}
              {readOnlyRow('Claim window', `${program.claim_ttl_days} days`)}
            </div>
          )}
        </div>

        {/* Reward ladder */}
        <div className="space-y-6">
          <div className="card overflow-hidden p-0">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Reward ladder</h2>
              {isDraft && (
                <button
                  onClick={() => setRungForm({ tier: null })}
                  className="btn-primary flex items-center gap-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  Add rung
                </button>
              )}
            </div>

            {tiers.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-gray-500">
                No rungs yet — a program needs at least one stamp rung to publish.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trigger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name nl / en
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valid
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {isDraft ? 'Actions' : <span className="sr-only">Actions</span>}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tiers.map((tier) => (
                    <tr key={tier.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {tier.trigger === 'WELCOME' ? 'WELCOME' : `STAMP ${tier.threshold}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {REWARD_TYPE_LABEL[tier.reward_type]}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div>{tier.name_nl}</div>
                        <div className="text-xs text-gray-500">{tier.name_en}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {tier.validity_days} d
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {tier.active ? '✓' : 'off'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                        {isDraft && (
                          <>
                            <button
                              onClick={() => setRungForm({ tier })}
                              className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                            >
                              <PencilIcon className="h-4 w-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => void handleDeleteTier(tier)}
                              className="text-red-600 hover:text-red-900 inline-flex items-center gap-1"
                            >
                              <TrashIcon className="h-4 w-4" />
                              Remove
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {isDraft && rungForm && (
            <RungForm
              key={rungForm.tier?.id ?? 'new'}
              program={program}
              tier={rungForm.tier}
              categories={categories}
              onClose={() => setRungForm(null)}
            />
          )}
        </div>
      </div>

      {publishing && (
        <PublishDialog
          program={program}
          currentProgram={currentProgram}
          onClose={() => setPublishing(false)}
        />
      )}
    </div>
  )
}

export default LoyaltyProgramEditorPage
